import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/** @jsx jsx */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { jsx } from '@emotion/react';
import { FormattedMessage } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import { Analytics, UFO } from '../../analytics';
import { ReactionStatus } from '../../types';
import { i18n } from '../../shared';
import { Reaction } from '../Reaction';
import { ReactionsDialog } from '../ReactionDialog';
import { ReactionPicker } from '../ReactionPicker';
import * as styles from './styles';
import { isSampled } from '../../analytics/analytics';
import { SAMPLING_RATE_REACTIONS_RENDERED_EXP } from '../../shared/constants';

/**
 * Set of all available UFO experiences relating to reactions dialog
 */
export var ufoExperiences = {
  /**
   * Expeirence when a reaction dialog is opened
   */
  openDialog: UFO.ReactionDialogOpened,
  /**
   * Experience when a reaction dialog is closed
   */
  closeDialog: UFO.ReactionDialogClosed,
  /**
   * Experience when a reaction changed/fetched from inside the modal dialog
   */
  selectedReactionChangeInsideDialog: UFO.ReactionDialogSelectedReactionChanged
};

/**
 * Test id for wrapper Reactions div
 */
export var RENDER_REACTIONS_TESTID = 'render-reactions';
/**
 * Test id for the view all reacted user to trigger the dialog
 */
export var RENDER_VIEWALL_REACTED_USERS_DIALOG = 'viewall-reacted-users-dialog';
/**
 * Get content of the tooltip
 */
export function getTooltip(status, errorMessage) {
  switch (status) {
    case ReactionStatus.error:
      return errorMessage || jsx(FormattedMessage, i18n.messages.unexpectedError);
    // When reaction is not available don't show any tooltip (e.g. Archive page in Confluence)
    case ReactionStatus.disabled:
      return null;
    case ReactionStatus.notLoaded:
    case ReactionStatus.loading:
      return jsx(FormattedMessage, i18n.messages.loadingReactions);
    case ReactionStatus.ready:
      return jsx(FormattedMessage, i18n.messages.addReaction);
  }
}

/**
 * Renders list of reactions
 */
export var Reactions = /*#__PURE__*/React.memo(function (_ref) {
  var _ref$flash = _ref.flash,
    flash = _ref$flash === void 0 ? {} : _ref$flash,
    status = _ref.status,
    errorMessage = _ref.errorMessage,
    loadReaction = _ref.loadReaction,
    quickReactionEmojis = _ref.quickReactionEmojis,
    pickerQuickReactionEmojiIds = _ref.pickerQuickReactionEmojiIds,
    _ref$getReactionDetai = _ref.getReactionDetails,
    getReactionDetails = _ref$getReactionDetai === void 0 ? function () {} : _ref$getReactionDetai,
    _ref$onReactionHover = _ref.onReactionHover,
    onReactionHover = _ref$onReactionHover === void 0 ? function () {} : _ref$onReactionHover,
    onSelection = _ref.onSelection,
    _ref$reactions = _ref.reactions,
    reactions = _ref$reactions === void 0 ? [] : _ref$reactions,
    emojiProvider = _ref.emojiProvider,
    allowAllEmojis = _ref.allowAllEmojis,
    onReactionClick = _ref.onReactionClick,
    allowUserDialog = _ref.allowUserDialog,
    _ref$onDialogOpenCall = _ref.onDialogOpenCallback,
    onDialogOpenCallback = _ref$onDialogOpenCall === void 0 ? function () {} : _ref$onDialogOpenCall,
    _ref$onDialogCloseCal = _ref.onDialogCloseCallback,
    onDialogCloseCallback = _ref$onDialogCloseCal === void 0 ? function () {} : _ref$onDialogCloseCal,
    _ref$onDialogSelectRe = _ref.onDialogSelectReactionCallback,
    onDialogSelectReactionCallback = _ref$onDialogSelectRe === void 0 ? function () {} : _ref$onDialogSelectRe,
    _ref$emojiPickerSize = _ref.emojiPickerSize,
    emojiPickerSize = _ref$emojiPickerSize === void 0 ? 'medium' : _ref$emojiPickerSize,
    _ref$miniMode = _ref.miniMode,
    miniMode = _ref$miniMode === void 0 ? false : _ref$miniMode;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    selectedEmojiId = _useState2[0],
    setSelectedEmojiId = _useState2[1];
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var openTime = useRef();
  var renderTime = useRef();
  useEffect(function () {
    if (status === ReactionStatus.notLoaded) {
      loadReaction();
    }
  }, [status, loadReaction]);
  useEffect(function () {
    if (status !== ReactionStatus.ready) {
      renderTime.current = Date.now();
    } else {
      if (isSampled(SAMPLING_RATE_REACTIONS_RENDERED_EXP)) {
        var _renderTime$current;
        Analytics.createAndFireSafe(createAnalyticsEvent, Analytics.createReactionsRenderedEvent, (_renderTime$current = renderTime.current) !== null && _renderTime$current !== void 0 ? _renderTime$current : Date.now() //renderTime.current can be null during unit test cases
        );
      }
      renderTime.current = undefined;
    }
  }, [createAnalyticsEvent, status]);
  var handleReactionMouseEnter = useCallback(function (emojiId) {
    getReactionDetails(emojiId);
    onReactionHover(emojiId);
  }, [getReactionDetails, onReactionHover]);
  var handleReactionFocused = useCallback(function (emojiId) {
    getReactionDetails(emojiId);
  }, [getReactionDetails]);
  var handlePickerOpen = useCallback(function () {
    openTime.current = Date.now();
    Analytics.createAndFireSafe(createAnalyticsEvent, Analytics.createPickerButtonClickedEvent, reactions.length);
  }, [createAnalyticsEvent, reactions]);
  var handleOnCancel = useCallback(function () {
    Analytics.createAndFireSafe(createAnalyticsEvent, Analytics.createPickerCancelledEvent, openTime.current);
    openTime.current = undefined;
  }, [createAnalyticsEvent]);
  var handleOnMore = useCallback(function () {
    Analytics.createAndFireSafe(createAnalyticsEvent, Analytics.createPickerMoreClickedEvent, openTime.current);
  }, [createAnalyticsEvent]);
  var handleOnSelection = useCallback(function (emojiId, source) {
    Analytics.createAndFireSafe(createAnalyticsEvent, Analytics.createReactionSelectionEvent, source, emojiId, reactions.find(function (reaction) {
      return reaction.emojiId === emojiId;
    }), openTime.current);
    openTime.current = undefined;
    onSelection(emojiId);
  }, [createAnalyticsEvent, onSelection, reactions]);

  /**
   * event handler to open selected reaction from tooltip
   * @param emojiId selected emoji id
   */
  var handleOpenReactionsDialog = function handleOpenReactionsDialog(emojiId) {
    // ufo start opening reaction dialog
    ufoExperiences.openDialog.start();
    setSelectedEmojiId(emojiId);
    onDialogOpenCallback(emojiId, 'tooltip');

    // ufo opening reaction dialog success
    ufoExperiences.openDialog.success({
      metadata: {
        emojiId: emojiId,
        source: 'Reactions',
        reason: 'Opening dialog from emoji tooltip link successfully'
      }
    });
  };

  /**
   * Event handler to oepn all reactions link button
   */
  var handleOpenAllReactionsDialog = function handleOpenAllReactionsDialog() {
    // ufo start opening reaction dialog
    ufoExperiences.openDialog.start();
    var emojiId = reactions[0].emojiId;
    getReactionDetails(emojiId);
    setSelectedEmojiId(emojiId);
    onDialogOpenCallback(emojiId, 'button');

    // ufo opening reaction dialog success
    ufoExperiences.openDialog.success({
      metadata: {
        emojiId: emojiId,
        source: 'Reactions',
        reason: 'Opening all reactions dialog link successfully'
      }
    });
  };
  var handleCloseReactionsDialog = function handleCloseReactionsDialog(e, analyticsEvent) {
    // ufo closing opening reaction dialog
    ufoExperiences.closeDialog.start();
    setSelectedEmojiId('');
    onDialogCloseCallback(e, analyticsEvent);

    // ufo closing reaction dialog success
    ufoExperiences.closeDialog.success({
      metadata: {
        source: 'Reactions',
        reason: 'Closing reactions dialog successfully'
      }
    });
  };
  var handleSelectReactionInDialog = function handleSelectReactionInDialog(emojiId, analyticsEvent) {
    // ufo selected reaction inside the modal dialog
    ufoExperiences.selectedReactionChangeInsideDialog.start();
    handleReactionMouseEnter(emojiId);
    onDialogSelectReactionCallback(emojiId, analyticsEvent);

    // ufo selected reaction inside the modal dialog success
    ufoExperiences.selectedReactionChangeInsideDialog.success({
      metadata: {
        emojiId: emojiId,
        source: 'Reactions',
        reason: 'Selected Emoji changed'
      }
    });
  };

  /**
   * Get the reactions that we want to render are any reactions with a count greater than zero as well as any default emoji not already shown
   */
  var memorizedReactions = useMemo(function () {
    //
    /**
     * If reactions not empty, don't show quick reactions Pre defined emoji or if its empty => return the current list of reactions
     */
    if (reactions.length > 0 || !quickReactionEmojis) {
      return reactions;
    }

    // add any missing default reactions
    var ari = quickReactionEmojis.ari,
      containerAri = quickReactionEmojis.containerAri,
      emojiIds = quickReactionEmojis.emojiIds;
    var items = emojiIds.filter(function (emojiId) {
      return !reactions.some(function (reaction) {
        return reaction.emojiId === emojiId;
      });
    }).map(function (emojiId) {
      return {
        ari: ari,
        containerAri: containerAri,
        emojiId: emojiId,
        count: 0,
        reacted: false
      };
    });
    return reactions.concat(items);
  }, [quickReactionEmojis, reactions]);
  return jsx("div", {
    css: styles.wrapperStyle,
    "data-testid": RENDER_REACTIONS_TESTID
  }, memorizedReactions.map(function (reaction) {
    return jsx(Reaction, {
      key: reaction.emojiId,
      reaction: reaction,
      emojiProvider: emojiProvider,
      onClick: onReactionClick,
      onMouseEnter: handleReactionMouseEnter,
      onFocused: handleReactionFocused,
      flash: flash[reaction.emojiId],
      handleUserListClick: handleOpenReactionsDialog,
      allowUserDialog: allowUserDialog
    });
  }), jsx(ReactionPicker, {
    css: styles.reactionPickerStyle,
    emojiProvider: emojiProvider,
    allowAllEmojis: allowAllEmojis,
    pickerQuickReactionEmojiIds: pickerQuickReactionEmojiIds,
    disabled: status !== ReactionStatus.ready,
    onSelection: handleOnSelection,
    onOpen: handlePickerOpen,
    onCancel: handleOnCancel,
    onShowMore: handleOnMore,
    tooltipContent: getTooltip(status, errorMessage),
    emojiPickerSize: emojiPickerSize,
    miniMode: miniMode
  }), allowUserDialog && reactions.length > 0 && jsx(Tooltip, {
    content: jsx(FormattedMessage, i18n.messages.seeWhoReactedTooltip),
    hideTooltipOnClick: true
  }, jsx(Button, {
    appearance: "subtle-link",
    onClick: handleOpenAllReactionsDialog,
    css: styles.seeWhoReacted,
    testId: RENDER_VIEWALL_REACTED_USERS_DIALOG
  }, jsx(FormattedMessage, i18n.messages.seeWhoReacted))), jsx(ModalTransition, null, !!selectedEmojiId && jsx(ReactionsDialog, {
    selectedEmojiId: selectedEmojiId,
    reactions: memorizedReactions,
    emojiProvider: emojiProvider,
    handleCloseReactionsDialog: handleCloseReactionsDialog,
    handleSelectReaction: handleSelectReactionInDialog
  })));
});